import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990ShcN = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990 Schedule N: Liquidation, Termination, Dissolution"
          description="Explore Form 990/990-EZ Schedule N, addressing Liquidation, Termination, and Dissolution. Ensure accurate reporting for a seamless tax compliance process."
          keywords=""
          canonical="resources/tax-exempt/form-990-schedule-n/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule N</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Report Liquidation or Dissolution Information with TaxZerone!
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-n/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Schedule N at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Form 990 Schedule N
                        </strong>{' '}
                        reports an organization's{' '}
                        <strong style={{ fontWeight: 600 }}>
                          liquidation, termination, dissolution, or significant
                          disposition of assets
                        </strong>{' '}
                        and provides details about the assets involved and their
                        recipients.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Organizations filing{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 990 or Form 990-EZ{' '}
                        </strong>{' '}
                        generally must complete Schedule N when they{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          liquidate, terminate, dissolve, or dispose of more
                          than 25% of their net assets
                        </strong>{' '}
                        during the tax year.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is a Significant Disposition?
                        </strong>{' '}
                        A significant disposition generally means a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          sale, exchange, transfer, or other disposition of more
                          than 25% of the fair market value of the
                          organization's net assets{' '}
                        </strong>{' '}
                        during the applicable period.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?{' '}
                        </strong>{' '}
                        Schedule N reports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          assets distributed, transaction expenses, distribution
                          dates, fair market values, recipients, and information
                          about successor or transferee organizations,
                        </strong>{' '}
                        as applicable.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule N?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule N?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#scha" sx={{ color: '#0202ff' }}>
                          Schedule N Filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule N filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Schedule N?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>Schedule N, </strong>
                  <i>
                    {' '}
                    Liquidation, Termination, Dissolution, or Significant
                    Disposition of Assets{' '}
                  </i>{' '}
                  is a supplementary schedule that accompanies the standard Form
                  990/
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-EZ
                  </Link>{' '}
                  and is filed annually by tax-exempt and nonprofits.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Any organization that files{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 990
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 990-EZ
                  </Link>{' '}
                  and is going out of existence or disposing of more than 25% of
                  its net assets through sale, exchange, or other disposition
                  must complete and attach Schedule E along with their annual
                  information return.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Schedule N?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Any exempt organization that answered “Yes” to Form 990, Part
                  IV, Checklist of Required Schedules, line 31 or 32, or Form
                  990-EZ, line 36, must complete and attach Schedule N
                  attachment to{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990
                  </Link>
                  /
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-EZ
                  </Link>
                  , as applicable.
                </Typography>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Choose{' '}
                    <strong style={{ fontWeight: 600 }}>
                      TaxZerone to complete your Schedule N{' '}
                    </strong>
                    filing requirements. <br />
                    Your journey to staying tax-compliant starts here!
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schn`}
                  >
                    Get Started
                  </Button>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="scha"
                >
                  Schedule N Filing Requirements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schn-details-img.webp" />
                  <source srcSet="/assets/images/f990-schn-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schn-details-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule N showing organization details and basic information "
                    width={760}
                    height={90}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  All Section{' '}
                  <Link
                    href="/resources/tax-exempt/501c3-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    501(c)(3) organizations
                  </Link>{' '}
                  that file Form 990/990-EZ must complete and attach Schedule N
                  and their{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-EZ return
                  </Link>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided Schedule N filing requirements for
                  each part.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I - Liquidation, Termination, or Dissolution
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schn-part1-img.webp" />
                  <source srcSet="/assets/images/f990-schn-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schn-part1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule N Part I showing lines 1–2, where an organization reports details about its liquidation, termination, or dissolution."
                    width={760}
                    height={419}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  List any property, money, or items your organization gave away
                  when it closed, merged, or combined with another group.
                  Include each type of asset, and if you paid for professional
                  services like lawyers or accountants to help with the process,
                  list those expenses too.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Group assets by type and describe them. List big professional
                  fees (over $10,000) separately, like lawyer or accountant
                  fees. Don’t include broker fees here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Date the asset was given, or the fee was paid.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter how much the asset is worth or how much you paid for the
                  professional service. Keep it simple, just the dollar
                  amount.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Explain how you figured out that value.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (e &amp; f)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Name, address, and EIN of the recipient. You don’t need to
                  list members individually group them if it’s a membership
                  organization.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (g)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS code showing the recipient is tax-exempt (like{' '}
                  <Link
                    href="/resources/tax-exempt/501c3-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    501(c)(3)
                  </Link>
                  ) or notes if it’s a government agency or other type of
                  organization.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If any of your current leaders or key staff like officers,
                  directors, trustees, or important employees will have a role
                  in the organization receiving your assets, you need to report
                  it. This includes:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If they are on board or act as trustees.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2b
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If they work, there as an employee or contractor.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2c
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Own a part of the organization, directly or indirectly.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2d
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Receive any payment or benefit because of the transfer, like
                  severance or “change of control” payments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2e
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization answered “Yes” to any of the questions in
                  lines <span style={{ fontWeight: 600 }}> 2a through 2d</span>,
                  list the name of the person involved and provide a brief
                  explanation in Part III.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schn-part1-liquid-img.webp" />
                  <source srcSet="/assets/images/f990-schn-part1-liquid-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schn-part1-liquid-img.png"
                    loading="lazy"
                    alt="Part I - Form 990 Schedule N Part I showing lines 3-6, where an organization reports details about its liquidation, termination, or dissolution."
                    width={760}
                    height={123}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check “Yes” if your organization gave out its assets exactly
                  the way its own rules say it should.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check “Yes” if the law requires your organization to tell the
                  state (like the attorney general) that it plans to close or
                  shut down.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4b
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Mark “Yes” if the organization let the state know it is
                  closing or ending its operations.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check “Yes” if your organization has paid off all its bills
                  and obligations before closing, following state rules.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Mark “Yes” if your organization had any tax-exempt bonds
                  during the year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6b
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  Mark “Yes” if all those bonds were fully paid off, canceled,
                  or otherwise resolved during the year. Leave blank if you
                  don’t have bonds.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6c
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you answered “Yes” on 6b, explain in Part III how the bonds
                  were settled. Include details if anything wasn’t handled
                  according to the law, or if bonds were avoided by transferring
                  assets to another nonprofit including recipient names, bond
                  IDs (CUSIP), and a short description of the arrangement.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II - Sale, Exchange, Disposition, or Other Transfer of
                  More Than 25% of the Organization&apos;s Assets
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schn-part2-img.webp" />
                  <source srcSet="/assets/images/f990-schn-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schn-part2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule N Part II-Sale, Exchange, Disposition, or Other Transfer of More Than 25% of the Organization’s Assets Line 1 and Line 2"
                    width={760}
                    height={367}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    List any property, money, or items your organization gave
                    away when it closed, merged, or combined with another group.
                    Include each type of asset, and if you paid for professional
                    services like lawyers or accountants to help with the
                    process, list those expenses too.
                  </li>
                  <li>
                    Line 1, columns (a) through (g) in Part II have already been
                    explained in Part I, so kindly refer to that section for the
                    details.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 16, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If any of your current leaders or key staff like officers,
                  directors, trustees, or important employees will have a role
                  in the organization receiving your assets, you need to report
                  it. This includes:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If they are on board or act as trustees.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2b
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If they work, there as an employee or contractor.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2c
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Own a part of the organization, directly or indirectly.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  State whether any officer, director, trustee, or key employee
                  received or became eligible to receive compensation or similar
                  payments as a result of the organization’s significant
                  transfer or sale of assets.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the organization answered “Yes” to any of the questions in
                  lines <span style={{ fontWeight: 600 }}> 2a through 2d,</span>{' '}
                  list the name of the person involved and provide a brief
                  explanation in Part III.
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>Date of distribution</li>
                  <li style={{ marginBottom: 16 }}>
                    The fair market value of the asset(s) distributed or the
                    amount of transaction expenses
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Method of determining FMV for asset distributed or
                    transaction expenses
                  </li>
                  <li style={{ marginBottom: 16 }}>EIN of recipient</li>
                  <li style={{ marginBottom: 16 }}>
                    Name and address of the recipient
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    IRC section of recipient or type of entity
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In need of additional space for reporting information, Part II
                  of the schedule can be duplicated based on the requirement.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III - Supplemental Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schn-part3-img.webp" />
                  <source srcSet="/assets/images/f990-schn-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schn-part3-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule N- Part III Details of Supplemental Information"
                    width={760}
                    height={361}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This part can be used by the organization to provide the
                  required explanation on lines 2e, 3, and 6c of Part I,
                  Liquidation, Termination, or Dissolution and line 2e of Part
                  II, Sale, Exchange, Disposition, or Other Transfer of More
                  Than 25% of the Organization’s Assets.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  The part can also be duplicated by the 990 and 990-EZ filers
                  if more space is needed to provide additional information.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule N filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>TaxZerone</strong> is an
                  IRS-authorized e-file service provider; meaning you get
                  instant updates on your 990/990-EZ return filing status. We
                  ensure help is available at every step to provide you with an
                  easy e-filing experience!
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  At TaxZerone, we go beyond just filing – we&apos;re here to
                  make your entire experience seamless and stress-free.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here&apos;s how your Form 990/990-EZ return with Schedule N
                  attachment is transmitted to the IRS - just 3 simple steps!
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Provide Organization Details -
                    </strong>{' '}
                    Choose the tax year for which you want to file a return, and
                    provide your organization&apos;s details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Preview the Return -
                    </strong>{' '}
                    Complete Schedule N and preview the information provided in
                    the return for accuracy before transmitting.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Transmit to the IRS -
                    </strong>{' '}
                    Transmit Schedule N along with your{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      990
                    </Link>
                    /990-EZ return to the IRS and get the acceptance in just a
                    few hours.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Even if the IRS rejects your exempt information return for any
                  reason, you can correct and retransmit it to the IRS for free!
                </Typography>
                {/* <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mb: 5 }}
                  
                >
                  <strong style={{fontWeight:600}} >Note:</strong> Don&apos;t include in Part VI the names of any
                  donors, grantors, or contributors because Part VI will be made
                  available for public inspection.
                </Alert> */}
                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'column' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        <strong style={{ fontWeight: 600 }}>
                          Ready to attach Schedule N along with your 990/990-EZ
                          return with TaxZerone?
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        Make the e-filing process simple by clicking the button
                        below.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 300, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schn`}
                      >
                        E-File Your Form 990/990-EZ
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/f990sn.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule N (Form 990 or 990-EZ) – Liquidation,
                          Termination, Dissolution, or Significant Disposition
                          of Assets
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990 – Return of Organization Exempt From
                          Income Tax
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What are the two parts of Schedule N?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography>Schedule N consists of two parts:</Typography>
                    <List>
                      <ListItem>
                        <Box>
                          <span style={{ fontWeight: 600 }}> Part I:</span> This
                          part is for organizations that have completely
                          liquidated, terminated, or dissolved and ceased
                          operations during the tax year.
                        </Box>
                      </ListItem>
                      <ListItem>
                        <Box>
                          <span style={{ fontWeight: 600 }}> Part II:</span>{' '}
                          This part is for organizations that are still in the
                          process of winding up their affairs at the end of the
                          tax year, but have not yet completely liquidated,
                          terminated, or dissolved and ceased operations.
                        </Box>
                      </ListItem>
                    </List>
                    <Typography>
                      The schedule also has another part (Supplemental
                      Information) that can be used by the organization to
                      provide additional explanations on questions answered in
                      Parts I and II.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. When is Schedule N due?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    The{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-due-dates/"
                      sx={{ color: '#0202ff' }}
                    >
                      due date
                    </Link>{' '}
                    to file Schedule N is the same as filing Form 990 or{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-ez/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 990-EZ
                    </Link>
                    . For most organizations, this is the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      15th day of the 5th month
                    </strong>{' '}
                    after the close of the tax year. For organizations that
                    follow the calendar year (ending on December 31), the due
                    date is{' '}
                    <strong style={{ fontWeight: 600 }}>May 15th.</strong>
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What is considered a &quot;significant disposition of
                  assets&quot;?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    A significant disposition of the organization&apos;s net
                    assets includes a sale, exchange, disposition, or other
                    transfer of more than 25% of the FMV of its net assets
                    during the tax year, regardless of whether the organization
                    received full and adequate consideration.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990ShcN;
